import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setAccessToken, setUnauthenticatedHandler } from "./api";

/**
 * Authentication.
 *
 * Supabase owns the identity flow; this module owns the bridge between it and the
 * rest of the app. The access token is pushed into the API client on every change
 * rather than read from a hook at call time, so a request fired from outside React
 * (a prefetch, a retry) still carries the current token.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set at build time.",
  );
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "pkce",
  },
});

export interface AngemeldeterBenutzer {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

interface AuthContextValue {
  benutzer: AngemeldeterBenutzer | null;
  /** Null until the first session check resolves; distinguishes "loading" from "signed out". */
  bereit: boolean;
  anmeldenMitGoogle: () => Promise<void>;
  anmeldenMitMicrosoft: () => Promise<void>;
  anmeldenMitEmail: (email: string, passwort: string) => Promise<void>;
  abmelden: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toBenutzer(session: Session | null): AngemeldeterBenutzer | null {
  const user = session?.user;
  if (!user?.email) return null;
  const meta = user.user_metadata as Record<string, unknown> | undefined;
  return {
    id: user.id,
    email: user.email,
    name:
      (typeof meta?.full_name === "string" && meta.full_name) ||
      (typeof meta?.name === "string" && meta.name) ||
      user.email,
    avatarUrl:
      (typeof meta?.avatar_url === "string" && meta.avatar_url) ||
      (typeof meta?.picture === "string" && meta.picture) ||
      null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [benutzer, setBenutzer] = useState<AngemeldeterBenutzer | null>(null);
  const [bereit, setBereit] = useState(false);

  useEffect(() => {
    let aktiv = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!aktiv) return;
      setAccessToken(data.session?.access_token ?? null);
      setBenutzer(toBenutzer(data.session));
      setBereit(true);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setAccessToken(session?.access_token ?? null);
      setBenutzer(toBenutzer(session));
      setBereit(true);
    });

    return () => {
      aktiv = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // The API client reports a 401 back to us rather than redirecting itself, so
    // there is one place that decides what "signed out" means for the UI.
    setUnauthenticatedHandler(() => {
      void supabase.auth.signOut();
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  const anmeldenMitProvider = useCallback(async (provider: "google" | "azure") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        // Azure needs the scopes named explicitly to return an email claim.
        ...(provider === "azure" ? { scopes: "email openid profile" } : {}),
      },
    });
    if (error) throw new Error(error.message);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      benutzer,
      bereit,
      anmeldenMitGoogle: () => anmeldenMitProvider("google"),
      anmeldenMitMicrosoft: () => anmeldenMitProvider("azure"),
      anmeldenMitEmail: async (email, passwort) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password: passwort });
        if (error) throw new Error(uebersetzeAuthFehler(error.message));
      },
      abmelden: async () => {
        await supabase.auth.signOut();
      },
    }),
    [benutzer, bereit, anmeldenMitProvider],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

/** Supabase returns English strings; the user should not see them. */
function uebersetzeAuthFehler(message: string): string {
  if (/invalid login credentials/i.test(message)) return "E-Mail-Adresse oder Passwort ist falsch.";
  if (/email not confirmed/i.test(message)) return "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.";
  if (/rate limit/i.test(message)) return "Zu viele Versuche. Bitte warten Sie einen Moment.";
  return "Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.";
}
